import React, { useState } from "react";
import mixpanel from "mixpanel-browser";

import { DashboardAPI } from "utils/api";

import Dialog, { CloseDialog } from "components/Dialog";
import Input from "components/Input";
import Button from "components/Button";

import classes from "./styles.module.scss";

const TransferCreditsModal = ({
    credits,
    isOpen,
    closeModal,
    transferTo,
    transferToName,
    setAlert,
    transferFrom,
    organizationId,
    updateUser,
}) => {
    const [creditsToTransfer, setCreditsToTransfer] = useState(0);
    const [isTransferring, setIsTransferring] = useState(false);

    const amount = Number(creditsToTransfer);
    const isInvalidAmount = !Number.isInteger(amount) || amount <= 0 || amount > credits;

    const handleClose = () => {
        if (isTransferring) return;
        setCreditsToTransfer(0);
        closeModal();
    };

    const handleTransfer = async (e) => {
        e.preventDefault();
        if (isTransferring || isInvalidAmount) return;

        setIsTransferring(true);

        try {
            const response = await DashboardAPI({
                method: "POST",
                url: "/transferCredits",
                data: {
                    transferFrom,
                    transferTo,
                    amount,
                    organizationId,
                },
            });

            // DashboardAPI returns the axios error object instead of throwing
            if (!response?.data || response?.isAxiosError) {
                const message =
                    response?.response?.data?.message ||
                    response?.message ||
                    "An error occurred while transferring credits";
                throw new Error(message);
            }

            const serverCredits = response.data?.creditSummary?.credits;
            updateUser({
                creditSummary: {
                    credits: serverCredits != null ? serverCredits : credits - amount,
                },
            });

            mixpanel.track("Credits Transfer", {
                UserId: transferFrom,
                TransferTo: transferTo,
                TransferToName: transferToName,
                Credits: amount,
                OrganizationId: organizationId,
            });

            setAlert({
                open: true,
                message: `Successfully transferred ${amount} credits to ${transferToName}`,
                anchorOrigin: { vertical: "top", horizontal: "center" },
                direction: "down",
                severity: "success",
            });
            setCreditsToTransfer(0);
            closeModal();
        } catch (error) {
            setAlert({
                open: true,
                message: error?.message || "An error occurred while transferring credits",
                anchorOrigin: { vertical: "top", horizontal: "center" },
                direction: "down",
                severity: "error",
            });
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <Dialog open={isOpen}>
            <CloseDialog closeActions={handleClose} />
            <div className={classes.modalContainer}>
                <h2 className={classes.modalTitle}>Transfer Credits</h2>
                <div className={classes.modalBodyContainer}>
                    <p>
                        You have {credits} credits available. How many would you like to transfer to{" "}
                        <strong>{transferToName}</strong>?
                    </p>
                    <Input
                        type="number"
                        label="Credits to transfer"
                        input={{
                            value: creditsToTransfer,
                            onChange: (value) => setCreditsToTransfer(value == null ? 0 : value),
                        }}
                        meta={{
                            error:
                                amount > credits
                                    ? "You don't have enough credits"
                                    : amount !== 0 && !Number.isInteger(amount)
                                      ? "Enter a whole number of credits"
                                      : null,
                            invalid: amount > credits || (amount !== 0 && !Number.isInteger(amount)),
                        }}
                    />
                </div>
                <div className={classes.buttonsContainer}>
                    <Button type="red" onClick={handleClose} disabled={isTransferring}>
                        Cancel
                    </Button>
                    <Button disabled={isInvalidAmount || isTransferring} onClick={handleTransfer}>
                        {isTransferring ? "Transferring..." : "Transfer"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default TransferCreditsModal;
